import { useEffect, useMemo, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import useUsers from 'contexts/basicData/useUsers';
import { formatDate } from 'utils/date-helpers';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { LongTitles, TLWidgetsEnum, UPDATE_INTERVAL } from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarDay,
  faCheckCircle,
  faExclamationCircle,
  faUser,
  faUsers,
  faWarning,
} from '@fortawesome/free-solid-svg-icons';
import useModalStack from 'contexts/modal/useModalStack';
import AvailabilityFormModal from 'pages/Employees/Availability/AvailabilityFormModal';
import styled from 'styled-components';
import TextButton from 'components/inputs/TextButton';
import useTranslations from 'contexts/basicData/useTranslations';
import { FieldTesterIncompleteDayPlanDTO } from 'api/model/fieldTesterIncompleteDayPlanDTO';
import { DayIncompleteReasonEnum } from 'api/model';
import { useTransportLogisticsGetFieldtestersWithOutsideHomeCounty } from 'api/transport-logistics/transport-logistics';
import { makeBooleanComparator } from 'utils/sorting';
import {
  Centered,
  getTableRowClassName,
} from 'components/Dashboard/table-helpers';

const MyTextButton = styled(TextButton)`
  padding: 0px;
`;

const TABLE_NAME = 'incomplete-day-plan';

const IncompleteDayPlanWidget = () => {
  const [fieldTestersOutsideHomeCounty, setFieldTestersOutsideHomeCounty] =
    useState<FieldTesterIncompleteDayPlanDTO[]>([]);
  const users = useUsers();
  const {
    dashboardState: { daterange, selectedBusinessUnits, searchText },
  } = useDashboardControls();

  const modalStack = useModalStack();
  const translations = useTranslations();

  const getAssignments =
    useTransportLogisticsGetFieldtestersWithOutsideHomeCounty(
      {
        startDate: daterange[0] ?? undefined,
        endDate: daterange[1] ?? undefined,
        businessUnitIDs: selectedBusinessUnits,
      },
      {
        query: {
          refetchInterval: UPDATE_INTERVAL,
        },
      }
    );

  useEffect(() => {
    if (getAssignments.data) {
      setFieldTestersOutsideHomeCounty(getAssignments.data);
    } else {
      setFieldTestersOutsideHomeCounty([] as FieldTesterIncompleteDayPlanDTO[]);
    }
  }, [getAssignments.data]);

  const filteredAssignments = useMemo(() => {
    if (!searchText.length) {
      return fieldTestersOutsideHomeCounty;
    }
    return fieldTestersOutsideHomeCounty
      ? fieldTestersOutsideHomeCounty.filter((fieldtester) => {
          return (users[fieldtester.userId]?.name ?? '')
            .toLowerCase()
            .includes(searchText.toLowerCase());
        })
      : [];
  }, [fieldTestersOutsideHomeCounty, searchText, users]);

  const columnSettings: ColumnSetting<FieldTesterIncompleteDayPlanDTO, any>[] =
    useMemo(() => {
      const columns: ColumnSetting<FieldTesterIncompleteDayPlanDTO, any>[] = [
        {
          head: 'Nr',
          render: (e, t, f, i) => i + 1,
          width: 15,
        },
        {
          head: 'Datum',
          render: (row) => (
            <span title={formatDate(row.date)}>
              {`${formatDate(row.date)}`}
            </span>
          ),
          width: 100,
          sortFunction: (a, b) => a.date.getTime() - b.date.getTime(),
        },
        {
          head: 'Fälttestare',
          render: (row) => {
            return (
              <span title={users[row.userId] ? users[row.userId].name : ''}>
                {users[row.userId] ? users[row.userId].name : ''}
              </span>
            );
          },
          width: 100,
          sortFunction: (a, b) =>
            (users[a.userId]?.name ?? '').localeCompare(
              users[b.userId]?.name ?? ''
            ),
        },
        {
          head: 'Telefonnummer',
          render: (row) => {
            return (
              <span
                title={users[row.userId] ? users[row.userId].phoneNumber : ''}
              >
                {users[row.userId] ? users[row.userId].phoneNumber : ''}
              </span>
            );
          },
          width: 100,
          sortFunction: (a, b) =>
            (users[a.userId]?.phoneNumber ?? '').localeCompare(
              users[b.userId]?.phoneNumber ?? ''
            ),
        },
        {
          head: 'Est. tid satt av',
          render: (row) => {
            const plannerNames = (row.estimatedDurationSetByUserIDs ?? [])
              .map((id) => users[id]?.name)
              .filter((name): name is string => !!name);
            if (plannerNames.length === 0) {
              return <span>-</span>;
            }
            if (plannerNames.length === 1) {
              return (
                <span title={plannerNames[0]}>
                  <FontAwesomeIcon icon={faUser} /> {plannerNames[0]}
                </span>
              );
            }
            return (
              <span title={plannerNames.join(', ')}>
                <FontAwesomeIcon icon={faUsers} />
                <b> ({plannerNames.length})</b>
                {plannerNames.join(', ')}
              </span>
            );
          },
          width: 100,
          sortFunction: (a, b) => {
            const aPlanners = (a.estimatedDurationSetByUserIDs ?? [])
              .map((id) => users[id]?.name ?? '')
              .join(', ');
            const bPlanners = (b.estimatedDurationSetByUserIDs ?? [])
              .map((id) => users[id]?.name ?? '')
              .join(', ');
            return aPlanners.localeCompare(bPlanners);
          },
        },
        {
          head: 'Varningar',
          render: (row) => {
            return (
              <span
                title={row.dayIncompleteReasons
                  .map(
                    (reason) => translations.fieldtestersDayIncomplete[reason]
                  )
                  .join(' ')}
              >
                {row.dayIncompleteReasons
                  .map(
                    (reason) => translations.fieldtestersDayIncomplete[reason]
                  )
                  .join(' ')}
              </span>
            );
          },
          width: 200,
          sortFunction: (a, b) =>
            a.dayIncompleteReasons
              .join(', ')
              .localeCompare(b.dayIncompleteReasons.join(' ')),
        },
        {
          head: ' ',
          render: (row) => {
            return row.dayIncompleteReasons.includes(
              DayIncompleteReasonEnum.EndingOutsideCounty
            ) ||
              row.dayIncompleteReasons.includes(
                DayIncompleteReasonEnum.StartingOutsideCounty
              ) ? (
              users[row.userId]?.isAllowedHalfdayPlanning ? (
                <Centered>
                  <FontAwesomeIcon
                    title="Fälttestaren får planeras ut halvdagar"
                    icon={faCheckCircle}
                    fontSize="18px"
                    color="green"
                  />
                </Centered>
              ) : (
                <Centered>
                  <FontAwesomeIcon
                    title="Fälttestaren får ej planeras ut halvdagar"
                    icon={faExclamationCircle}
                    fontSize="18px"
                    color="red"
                  />
                </Centered>
              )
            ) : null;
          },
          width: 20,
          sortFunction: makeBooleanComparator(
            (row) => users[row.userId]?.isAllowedHalfdayPlanning ?? false
          ),
        },
        {
          head: ' ',
          render: (row) =>
            row.dayIncompleteReasons.includes(
              DayIncompleteReasonEnum.EndingOutsideCounty
            ) && users[row.userId]?.canStayAtHotels === false ? (
              <Centered>
                <FontAwesomeIcon
                  title="OBS! Fälttestaren kan ej bo på hotell!"
                  icon={faWarning}
                  fontSize="18px"
                  color="red"
                />
              </Centered>
            ) : (
              ''
            ),
          width: 20,
        },

        {
          head: ' ',
          render: (row) => (
            <Centered>
              <MyTextButton>
                <FontAwesomeIcon
                  onClick={() => {
                    modalStack.push(
                      <AvailabilityFormModal
                        userId={row.userId}
                        initialDate={row.date}
                        name={users[row.userId]?.name ?? ''}
                        onClose={() => modalStack.pop()}
                        onSaved={() => modalStack.pop()}
                        viewOnly
                        showUserFields
                      />
                    );
                  }}
                  icon={faCalendarDay}
                  fontSize="18px"
                  style={{ cursor: 'pointer' }}
                />
              </MyTextButton>
            </Centered>
          ),
          width: 20,
        },
        //   {
        //     head: 'Klart',
        //     render: (row) => (
        //       <Centered>
        //         <Checkbox
        //           disabled={markAsReadStatus === 'loading'}
        //           onChange={async () => {
        //             setOngoingSaves([...ongoingSaves, row.id]);
        //             await markEventAsRead({ data: [row.id] });
        //             setOngoingSaves(ongoingSaves.filter((id) => id !== row.id));
        //           }}
        //         />
        //       </Centered>
        //     ),
        //     width: 30,
        //   },
      ];
      return columns;
    }, [modalStack, translations.fieldtestersDayIncomplete, users]);
  return (
    <Widget
      title={LongTitles[TLWidgetsEnum.IncompleteDayPlan]}
      count={fieldTestersOutsideHomeCounty.length}
      isLoading={getAssignments.isLoading}
      info="Fälttestarens dag har luckor i planeringen, där uppdragen antingen startar/slutar för långt från hemmet eller har uppdrag som startar/slutar för långt ifrån varandra."
    >
      {/* <SavingRowAnimationWrapper
        tableName={TABLE_NAME}
        savingIds={ongoingSaves}
      > */}
      <Table
        columnSettings={columnSettings}
        rows={filteredAssignments}
        rowClassName={(row) =>
          getTableRowClassName(TABLE_NAME, row.userId + row.date.getTime())
        }
        useColumnWidthAsFlexAmount
      />
      {/* </SavingRowAnimationWrapper> */}
    </Widget>
  );
};

export default IncompleteDayPlanWidget;
